import { useState } from 'react';
import { Star, Loader2, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from '../lib/toast';

// Star rating component
const StarRating = ({ value, onChange, label, description }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-[var(--theme-text-primary)]">{label}</label>
        <span className="text-sm text-[var(--theme-text-muted)]">{value}/5</span>
      </div>
      {description && (
        <p className="text-xs text-[var(--theme-text-muted)]">{description}</p>
      )}
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            className="p-1 transition-transform hover:scale-110"
          >
            <Star
              className={`w-6 h-6 transition-colors ${
                star <= (hover || value)
                  ? 'fill-[#F59E0B] text-[#F59E0B]'
                  : 'text-[var(--theme-border)]'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

// Review form for buyers reviewing owners
export const BuyerReviewForm = ({ matchId, revieweeUsername, onSuccess, onCancel, api }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    overall_rating: 0,
    communication_rating: 0,
    item_accuracy_rating: 0,
    item_quality_rating: 0,
    would_transact_again: null,
    comment: '',
  });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.overall_rating === 0 || formData.communication_rating === 0 ||
        formData.item_accuracy_rating === 0 || formData.item_quality_rating === 0) {
      toast.error('Please provide all ratings');
      return;
    }
    
    if (formData.would_transact_again === null) {
      toast.error('Please indicate if you would transact again');
      return;
    }
    
    setLoading(true);
    
    try {
      await api.post('/reviews/', {
        match_id: matchId,
        ...formData,
      });
      
      toast.success('Review submitted! Thank you for your feedback.');
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center pb-4 border-b border-[var(--theme-border)]">
        <h3 className="text-lg font-bold text-[var(--theme-text-primary)]">
          Rate your experience with {revieweeUsername}
        </h3>
        <p className="text-sm text-[var(--theme-text-secondary)] mt-1">
          As a buyer, share your experience with this seller
        </p>
      </div>
      
      <div className="space-y-5">
        <StarRating
          label="Overall Experience"
          description="How was your overall experience?"
          value={formData.overall_rating}
          onChange={(v) => setFormData(prev => ({ ...prev, overall_rating: v }))}
        />
        
        <StarRating
          label="Communication"
          description="How responsive and clear was the seller?"
          value={formData.communication_rating}
          onChange={(v) => setFormData(prev => ({ ...prev, communication_rating: v }))}
        />
        
        <StarRating
          label="Item Accuracy"
          description="How accurately was the item described?"
          value={formData.item_accuracy_rating}
          onChange={(v) => setFormData(prev => ({ ...prev, item_accuracy_rating: v }))}
        />
        
        <StarRating
          label="Item Quality"
          description="What was the actual quality of the item?"
          value={formData.item_quality_rating}
          onChange={(v) => setFormData(prev => ({ ...prev, item_quality_rating: v }))}
        />
        
        {/* Would transact again */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-[var(--theme-text-primary)]">
            Would you buy from this seller again?
          </label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, would_transact_again: true }))}
              className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                formData.would_transact_again === true
                  ? 'border-[#059669] bg-[#059669]/10 text-[#059669]'
                  : 'border-[var(--theme-border)] text-[var(--theme-text-secondary)] hover:border-[#059669]'
              }`}
            >
              {formData.would_transact_again === true && <CheckCircle className="w-4 h-4" />}
              Yes, definitely
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, would_transact_again: false }))}
              className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all ${
                formData.would_transact_again === false
                  ? 'border-[#DC2626] bg-[#DC2626]/10 text-[#DC2626]'
                  : 'border-[var(--theme-border)] text-[var(--theme-text-secondary)] hover:border-[#DC2626]'
              }`}
            >
              No
            </button>
          </div>
        </div>
        
        {/* Comment */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-[var(--theme-text-primary)]">
            Additional Comments <span className="text-[var(--theme-text-muted)]">(optional)</span>
          </label>
          <Textarea
            value={formData.comment}
            onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
            placeholder="Share your experience..."
            className="bg-[var(--theme-hover)] border-[var(--theme-border)] text-[var(--theme-text-primary)] min-h-[100px]"
            maxLength={500}
          />
          <p className="text-xs text-[var(--theme-text-muted)] text-right">
            {formData.comment.length}/500
          </p>
        </div>
      </div>
      
      <div className="flex gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1 rounded-full"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={loading}
          className="flex-1 bg-[#C9956C] hover:bg-[#B8845B] rounded-full"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Review'}
        </Button>
      </div>
    </form>
  );
};

// Review form for owners reviewing buyers
export const OwnerReviewForm = ({ matchId, revieweeUsername, onSuccess, onCancel, api }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    overall_rating: 0,
    communication_rating: 0,
    punctuality_rating: 0,
    payment_promptness_rating: 0,
    would_transact_again: null,
    comment: '',
  });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.overall_rating === 0 || formData.communication_rating === 0 ||
        formData.punctuality_rating === 0 || formData.payment_promptness_rating === 0) {
      toast.error('Please provide all ratings');
      return;
    }
    
    if (formData.would_transact_again === null) {
      toast.error('Please indicate if you would transact again');
      return;
    }
    
    setLoading(true);
    
    try {
      await api.post('/reviews/', {
        match_id: matchId,
        ...formData,
      });
      
      toast.success('Review submitted! Thank you for your feedback.');
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center pb-4 border-b border-[var(--theme-border)]">
        <h3 className="text-lg font-bold text-[var(--theme-text-primary)]">
          Rate your experience with {revieweeUsername}
        </h3>
        <p className="text-sm text-[var(--theme-text-secondary)] mt-1">
          As a seller, share your experience with this buyer
        </p>
      </div>
      
      <div className="space-y-5">
        <StarRating
          label="Overall Experience"
          description="How was your overall experience?"
          value={formData.overall_rating}
          onChange={(v) => setFormData(prev => ({ ...prev, overall_rating: v }))}
        />
        
        <StarRating
          label="Communication"
          description="How responsive and clear was the buyer?"
          value={formData.communication_rating}
          onChange={(v) => setFormData(prev => ({ ...prev, communication_rating: v }))}
        />
        
        <StarRating
          label="Punctuality"
          description="Was the buyer punctual when meeting?"
          value={formData.punctuality_rating}
          onChange={(v) => setFormData(prev => ({ ...prev, punctuality_rating: v }))}
        />
        
        <StarRating
          label="Payment Promptness"
          description="Did the buyer submit prompt payment?"
          value={formData.payment_promptness_rating}
          onChange={(v) => setFormData(prev => ({ ...prev, payment_promptness_rating: v }))}
        />
        
        {/* Would transact again */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-[var(--theme-text-primary)]">
            Would you sell to this buyer again?
          </label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, would_transact_again: true }))}
              className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                formData.would_transact_again === true
                  ? 'border-[#059669] bg-[#059669]/10 text-[#059669]'
                  : 'border-[var(--theme-border)] text-[var(--theme-text-secondary)] hover:border-[#059669]'
              }`}
            >
              {formData.would_transact_again === true && <CheckCircle className="w-4 h-4" />}
              Yes, definitely
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, would_transact_again: false }))}
              className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all ${
                formData.would_transact_again === false
                  ? 'border-[#DC2626] bg-[#DC2626]/10 text-[#DC2626]'
                  : 'border-[var(--theme-border)] text-[var(--theme-text-secondary)] hover:border-[#DC2626]'
              }`}
            >
              No
            </button>
          </div>
        </div>
        
        {/* Comment */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-[var(--theme-text-primary)]">
            Additional Comments <span className="text-[var(--theme-text-muted)]">(optional)</span>
          </label>
          <Textarea
            value={formData.comment}
            onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
            placeholder="Share your experience..."
            className="bg-[var(--theme-hover)] border-[var(--theme-border)] text-[var(--theme-text-primary)] min-h-[100px]"
            maxLength={500}
          />
          <p className="text-xs text-[var(--theme-text-muted)] text-right">
            {formData.comment.length}/500
          </p>
        </div>
      </div>
      
      <div className="flex gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1 rounded-full"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={loading}
          className="flex-1 bg-[#C9956C] hover:bg-[#B8845B] rounded-full"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Review'}
        </Button>
      </div>
    </form>
  );
};

// Review display card
export const ReviewCard = ({ review }) => {
  const isBuyerReview = review.reviewer_role === 'buyer';
  
  return (
    <div className="p-4 rounded-xl border border-[var(--theme-border)] bg-[var(--theme-card)]">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="font-medium text-[var(--theme-text-primary)]">
            {review.reviewer_username}
          </p>
          <p className="text-xs text-[var(--theme-text-muted)]">
            {isBuyerReview ? 'Bought from' : 'Sold to'} {review.reviewee_username}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Star className="w-4 h-4 fill-[#F59E0B] text-[#F59E0B]" />
          <span className="font-bold text-[var(--theme-text-primary)]">{review.overall_rating}</span>
        </div>
      </div>
      
      {/* Rating breakdown */}
      <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
        <div className="flex justify-between">
          <span className="text-[var(--theme-text-muted)]">Communication</span>
          <span className="text-[var(--theme-text-primary)]">{review.communication_rating}/5</span>
        </div>
        {isBuyerReview ? (
          <>
            <div className="flex justify-between">
              <span className="text-[var(--theme-text-muted)]">Item Accuracy</span>
              <span className="text-[var(--theme-text-primary)]">{review.item_accuracy_rating}/5</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--theme-text-muted)]">Item Quality</span>
              <span className="text-[var(--theme-text-primary)]">{review.item_quality_rating}/5</span>
            </div>
          </>
        ) : (
          <>
            <div className="flex justify-between">
              <span className="text-[var(--theme-text-muted)]">Punctuality</span>
              <span className="text-[var(--theme-text-primary)]">{review.punctuality_rating}/5</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--theme-text-muted)]">Payment</span>
              <span className="text-[var(--theme-text-primary)]">{review.payment_promptness_rating}/5</span>
            </div>
          </>
        )}
      </div>
      
      {review.comment && (
        <p className="text-sm text-[var(--theme-text-secondary)] italic border-t border-[var(--theme-border)] pt-3 mt-3">
          &quot;{review.comment}&quot;
        </p>
      )}
      
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-[var(--theme-border)]">
        <span className={`text-xs px-2 py-1 rounded-full ${
          review.would_transact_again 
            ? 'bg-[#059669]/10 text-[#059669]' 
            : 'bg-[#DC2626]/10 text-[#DC2626]'
        }`}>
          {review.would_transact_again ? '✓ Would transact again' : '✗ Would not transact again'}
        </span>
        <span className="text-xs text-[var(--theme-text-muted)]">
          {new Date(review.created_at).toLocaleDateString()}
        </span>
      </div>
    </div>
  );
};
